import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import MetricChart from '../../components/MetricChart';
import getQueryPresets from '../../data/queryPresets';
import { formatBits } from '../components/DashboardShared';
import { Panel, Button } from '../../components/design';

const CORE_API = '/api';

const categoryLabels = {
  system: 'System', mysql: 'MySQL', redis: 'Redis', nginx: 'Nginx',
  docker: 'Docker', httpd: 'HTTPD', php_fpm: 'PHP-FPM', smartctl: 'Smartctl',
  clickhouse: 'ClickHouse', gitlab: 'GitLab', process: 'Processes', wazuh: 'Wazuh',
  postgres: 'PostgreSQL', mongodb: 'MongoDB', kafka: 'Kafka', rabbitmq: 'RabbitMQ',
  haproxy: 'HAProxy',
};

export default function MetricsTab({ server, serverId, cpuSeries, cpuValue, memSeries, memValue, diskSeries, diskValue, netRxSeries, netTxSeries }) {
  const [customQuery, setCustomQuery] = useState('');
  const [queryTimeRange, setQueryTimeRange] = useState('1h');
  const [queryCategory, setQueryCategory] = useState('system');
  const [customQueryLoading, setCustomQueryLoading] = useState(false);
  const [customQueryResult, setCustomQueryResult] = useState(null);

  const { data: procsData } = useQuery({
    queryKey: ['servers', serverId, 'processes'],
    queryFn: () => fetch(`${CORE_API}/servers/${serverId}/processes?limit=20`).then(r => r.ok ? r.json() : { processes: [] }),
    enabled: !!serverId,
    refetchInterval: 30000,
    retry: 0,
  });
  const processes = procsData?.processes || [];

  const ip = server?.ip_address?.split('/')[0] || server?.ip_address || '';

  const netRxValue = netRxSeries?.length > 0 ? parseFloat(netRxSeries[netRxSeries.length - 1][1]) : null;
  const netTxValue = netTxSeries?.length > 0 ? parseFloat(netTxSeries[netTxSeries.length - 1][1]) : null;

  const metricColor = (val) => {
    if (!val) return 'var(--text-3)';
    const n = parseFloat(val);
    if (n > 90) return 'var(--danger)';
    if (n > 70) return 'var(--warning)';
    return 'var(--success)';
  };

  const runCustomQuery = async () => {
    if (!customQuery.trim()) return;
    setCustomQueryLoading(true);
    setCustomQueryResult(null);
    try {
      const hours = queryTimeRange === '15m' ? 0.25 : queryTimeRange === '1h' ? 1 : queryTimeRange === '6h' ? 6 : queryTimeRange === '24h' ? 24 : 1;
      const step = queryTimeRange === '15m' ? '15s' : queryTimeRange === '1h' ? '60s' : queryTimeRange === '6h' ? '5m' : '15m';
      const res = await fetch(`${CORE_API}/metrics/prometheus/range?query=${encodeURIComponent(customQuery)}&hours=${hours}&step=${step}`);
      const data = await res.json();
      setCustomQueryResult(data);
    } catch (e) {
      setCustomQueryResult({ error: e.message });
    }
    setCustomQueryLoading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Panel title="CPU Usage (%)" actions={<span style={{ fontWeight: 700, fontSize: 22, color: metricColor(cpuValue) }}>{cpuValue ? `${cpuValue}%` : 'N/A'}</span>}>
        <MetricChart data={cpuSeries} height={200} color="#6366f1" showGrid showLabels />
      </Panel>
      <Panel title="Memory Usage (%)" actions={<span style={{ fontWeight: 700, fontSize: 22, color: metricColor(memValue) }}>{memValue ? `${memValue}%` : 'N/A'}</span>}>
        <MetricChart data={memSeries} height={200} color="#8b5cf6" showGrid showLabels />
      </Panel>
      <Panel title="Disk Usage (%)" actions={<span style={{ fontWeight: 700, fontSize: 22, color: metricColor(diskValue) }}>{diskValue ? `${diskValue}%` : 'N/A'}</span>}>
        <MetricChart data={diskSeries} height={200} color="#06b6d4" showGrid showLabels />
      </Panel>

      <Panel title="Network Traffic (24h)"
        actions={<span className="mono" style={{ fontSize: 13, color: 'var(--text-2)' }}>
          <span style={{ color: 'var(--success)', fontWeight: 700 }}>↓ {netRxValue != null ? formatBits(netRxValue) : 'N/A'}</span>
          {' · '}
          <span style={{ color: 'var(--warning)', fontWeight: 700 }}>↑ {netTxValue != null ? formatBits(netTxValue) : 'N/A'}</span>
        </span>}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Receive</div>
            <MetricChart data={netRxSeries} height={140} color="#10b981" showGrid />
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Transmit</div>
            <MetricChart data={netTxSeries} height={140} color="#f59e0b" showGrid />
          </div>
        </div>
      </Panel>

      <Panel title="Top Processes" sub={processes.length > 0 ? `${processes.length} by CPU` : 'Live SSH snapshot'} noPad>
        {processes.length === 0 ? (
          <div className="empty">No process data</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Process</th><th>PID</th><th>CPU</th><th>MEM</th><th>RSS</th><th>User</th></tr>
              </thead>
              <tbody>
                {processes.map(p => (
                  <tr key={p.pid || p.command}>
                    <td><span className="mono" style={{ color: 'var(--text-1)', display: 'block', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.command}</span></td>
                    <td><span className="mono">{p.pid ?? '—'}</span></td>
                    <td><span className="mono" style={{ color: (p.cpu || 0) > 50 ? 'var(--danger)' : 'var(--text-2)' }}>{p.cpu?.toFixed?.(1) ?? p.cpu}%</span></td>
                    <td><span className="mono">{p.mem?.toFixed?.(1) ?? p.mem}%</span></td>
                    <td><span className="mono muted">{p.rss_kb ? `${Math.round(p.rss_kb / 1024)} MB` : '—'}</span></td>
                    <td><span className="muted">{p.user}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Prometheus Query">
        <div className="filter-bar" style={{ margin: 0 }}>
          <input type="text" value={customQuery} onChange={e => setCustomQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runCustomQuery()}
            placeholder="PromQL query..." style={{ flex: '1 1 200px', fontFamily: 'var(--mono)' }} />
          <select value={queryTimeRange} onChange={e => setQueryTimeRange(e.target.value)}>
            <option value="15m">15m</option>
            <option value="1h">1h</option>
            <option value="6h">6h</option>
            <option value="24h">24h</option>
          </select>
          <Button variant="primary" size="sm" onClick={runCustomQuery} disabled={customQueryLoading || !customQuery.trim()}>
            {customQueryLoading ? 'Running...' : 'Run'}
          </Button>
        </div>

        <div className="flex flex-wrap gap-1" style={{ margin: '12px 0' }}>
          {Object.entries(categoryLabels).map(([key, label]) => (
            <button key={key} onClick={() => setQueryCategory(key)}
              className="chip" style={queryCategory === key ? { borderColor: 'var(--accent-1)', color: 'var(--accent-1)' } : undefined}>
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2" style={{ marginBottom: 16 }}>
          {getQueryPresets(ip)[queryCategory]?.map(preset => (
            <button key={preset.label} onClick={() => setCustomQuery(preset.q)}
              className="chip">
              {preset.label}
            </button>
          ))}
        </div>

        {customQueryLoading && <div className="muted" style={{ textAlign: 'center', padding: '16px 0' }}>Running query...</div>}
        {customQueryResult && (
          <div style={{ marginTop: 16 }}>
            {customQueryResult.error ? (
              <div style={{ padding: '10px 14px', borderRadius: 10, fontSize: 12, background: 'rgba(248,113,113,0.10)', color: 'var(--danger)' }}>{customQueryResult.error}</div>
            ) : customQueryResult.data?.result?.length > 0 ? (
              <div>
                <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>{customQueryResult.data.result.length} series returned</div>
                {customQueryResult.data.result.map((series, idx) => {
                  const seriesData = series.values || [];
                  const lastVal = seriesData.length > 0 ? parseFloat(seriesData[seriesData.length - 1][1]).toFixed(2) : 'N/A';
                  return (
                    <div key={idx} style={{ marginBottom: 16 }}>
                      <div className="flex between center" style={{ marginBottom: 4 }}>
                        <div className="mono muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{Object.entries(series.metric || {}).map(([k, v]) => `${k}="${v}"`).join(', ')}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', marginLeft: 8 }}>{lastVal}</div>
                      </div>
                      <MetricChart data={seriesData} height={100} color={['#6366f1', '#8b5cf6', '#06b6d4', '#f59e0b', '#10b981', '#ef4444'][idx % 6]} showGrid showLabels />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="muted" style={{ textAlign: 'center', padding: '16px 0' }}>No data returned</div>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}
